import { useMemo, useState } from "react";
import { X, Search, User, Building2 } from "lucide-react";

/**
 * Modal con ricerca per selezionare un ospite o un'azienda già salvati.
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - kind: "guest" | "company"
 *  - items: array
 *  - onPick: (item) => void
 */
export default function RubricaPicker({ open, onClose, kind, items, onPick }) {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) => {
      if (kind === "company") {
        return (
          (it.ragione_sociale || "").toLowerCase().includes(needle) ||
          (it.partita_iva || "").toLowerCase().includes(needle) ||
          (it.email || "").toLowerCase().includes(needle)
        );
      }
      return (
        `${it.first_name || ""} ${it.last_name || ""}`.toLowerCase().includes(needle) ||
        (it.document_number || "").toLowerCase().includes(needle) ||
        (it.email || "").toLowerCase().includes(needle) ||
        (it.phone || "").toLowerCase().includes(needle)
      );
    });
  }, [items, q, kind]);

  if (!open) return null;

  const title = kind === "company" ? "Scegli azienda dalla rubrica" : "Scegli ospite dalla rubrica";
  const placeholder = kind === "company"
    ? "Cerca per ragione sociale, P. IVA o email…"
    : "Cerca per nome, documento, email o telefono…";

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="rubrica-picker-backdrop"
    >
      <div
        className="bg-white w-full sm:max-w-xl sm:rounded-2xl rounded-t-3xl max-h-[85vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="rubrica-picker"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            {kind === "company" ? <Building2 className="w-5 h-5 text-blue-700" /> : <User className="w-5 h-5 text-blue-700" />}
            <h2 className="font-heading font-semibold text-lg">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"
            data-testid="rubrica-picker-close"
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-200">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-600 bg-white"
              data-testid="rubrica-picker-search"
            />
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {filtered.length} {kind === "company" ? "aziende" : "ospiti"} in rubrica
          </p>
        </div>

        <div className="flex-1 overflow-y-auto" data-testid="rubrica-picker-list">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">
              {items.length === 0
                ? (kind === "company" ? "Nessuna azienda salvata ancora." : "Nessun ospite salvato ancora.")
                : "Nessun risultato per questa ricerca."}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((it) => (
                <li key={it.id}>
                  <button
                    type="button"
                    onClick={() => { onPick(it); onClose(); }}
                    className="w-full px-5 py-3 text-left hover:bg-blue-50 transition-colors flex items-start gap-3"
                    data-testid={`rubrica-picker-item-${it.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      {kind === "company" ? <Building2 className="w-4 h-4" /> : <User className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      {kind === "company" ? (
                        <>
                          <div className="font-medium text-slate-900 truncate">{it.ragione_sociale}</div>
                          <div className="text-xs text-slate-500 truncate">
                            {it.partita_iva ? `P. IVA ${it.partita_iva}` : "—"}
                            {it.email ? ` · ${it.email}` : ""}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="font-medium text-slate-900 truncate">{it.first_name} {it.last_name}</div>
                          <div className="text-xs text-slate-500 truncate">
                            {it.document_number ? `Doc. ${it.document_number}` : "—"}
                            {it.nationality ? ` · ${it.nationality}` : ""}
                            {it.email ? ` · ${it.email}` : ""}
                          </div>
                        </>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
